import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  HiXMark,
  HiArrowsPointingOut,
  HiArrowsPointingIn,
} from "react-icons/hi2";
/* The launcher wears the real orb — same disc, rings and mark as the hero,
   just small. Rendered with interactive={false} because the launcher itself
   is already the <button>. */
import { InstaPassOrb } from "./hailey/InstaPassOrb";
/* The chat panel is now the one from the approved preview
   (instapass-hailey-orb-preview), ported from TSX in ./hailey/.
   The old AIChatWidget is left in the tree, unimported, so this is a
   one-line revert if anything about the new panel misbehaves. */
import { HaileyChat } from "./hailey/HaileyChat";
import "./hailey/hailey-hero.css";
import { useInstaPassAIChat } from "./InstaPassAIChatContext";

/**
 * Floating AI entry — orb opens AIChatWidget panel. Open state syncs with InstaPassAIChatProvider when present.
 */
export default function InstaPassAIFloatingChat() {
  const chatCtx = useInstaPassAIChat();
  const [localOpen, setLocalOpen] = useState(false);
  const [localFullscreen, setLocalFullscreen] = useState(false);

  const open = chatCtx ? chatCtx.open : localOpen;
  const setOpen = chatCtx ? chatCtx.setOpen : setLocalOpen;
  const fullscreen = chatCtx ? chatCtx.fullscreen : localFullscreen;
  const openFullscreen = chatCtx
    ? chatCtx.openFullscreen
    : () => {
        setLocalOpen(true);
        setLocalFullscreen(true);
      };
  const closeFullscreen = chatCtx
    ? chatCtx.closeFullscreen
    : () => setLocalFullscreen(false);
  const closeChat = chatCtx
    ? chatCtx.closeChat
    : () => {
        setLocalOpen(false);
        setLocalFullscreen(false);
      };

  return (
    <>
      {!open ? (
        <motion.button
          type="button"
          onClick={() => setOpen(true)}
          aria-label="Open InstaPass AI"
          className="fixed bottom-24 right-6 z-[9998] flex h-16 w-16 items-center justify-center rounded-full border-0 bg-transparent p-0"
          whileHover={{ scale: 1.06 }}
          whileTap={{ scale: 0.94 }}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <InstaPassOrb state="idle" interactive={false} className="ip-orb--mini" />
        </motion.button>
      ) : null}

      <AnimatePresence>
        {open ? (
          <>
            <motion.button
              type="button"
              aria-label="Close InstaPass AI"
              className={`fixed inset-0 z-[9999] bg-black/50 backdrop-blur-[2px] ${
                fullscreen ? "bg-black/70" : ""
              }`}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => closeChat()}
            />
            <motion.div
              role="dialog"
              aria-label="InstaPass AI chat"
              aria-modal="true"
              className={
                fullscreen
                  ? "fixed inset-0 z-[10000] flex flex-col overflow-hidden bg-[#0B1028]"
                  : `fixed z-[10000] flex flex-col overflow-hidden rounded-3xl border border-[#2A3575] bg-[#0B1028] shadow-2xl
                inset-x-3 bottom-3 top-auto h-[min(620px,calc(100vh-1.5rem))]
                sm:inset-x-auto sm:right-6 sm:bottom-24 sm:top-auto sm:h-[min(620px,calc(100vh-8rem))] sm:w-[min(420px,calc(100vw-3rem))]`
              }
              initial={
                fullscreen
                  ? { opacity: 0 }
                  : { opacity: 0, y: 24, scale: 0.96 }
              }
              animate={
                fullscreen ? { opacity: 1 } : { opacity: 1, y: 0, scale: 1 }
              }
              exit={
                fullscreen
                  ? { opacity: 0 }
                  : { opacity: 0, y: 16, scale: 0.98 }
              }
              transition={{ type: "spring", stiffness: 380, damping: 32 }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Panel controls. Expand/collapse sits to the left of close,
                  the order every window control has used for thirty years —
                  destructive action last, so a mis-click costs you the size
                  rather than the conversation.

                  The fullscreen state already existed in the chat context but
                  nothing ever set it; these are the buttons that reach it. */}
              <div
                className={`absolute top-3 z-10 flex items-center gap-1.5 ${
                  fullscreen ? "right-4" : "right-3"
                }`}
              >
                <button
                  type="button"
                  onClick={() => (fullscreen ? closeFullscreen() : openFullscreen())}
                  aria-label={fullscreen ? "Exit full screen" : "Full screen"}
                  title={fullscreen ? "Exit full screen" : "Full screen"}
                  className="flex h-8 w-8 items-center justify-center rounded-lg bg-white/5 text-white/60 transition-colors hover:bg-white/10 hover:text-white"
                >
                  {fullscreen ? (
                    <HiArrowsPointingIn className="h-4 w-4" strokeWidth={2} />
                  ) : (
                    <HiArrowsPointingOut className="h-4 w-4" strokeWidth={2} />
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => closeChat()}
                  aria-label="Close chat"
                  title="Close"
                  className="flex h-8 w-8 items-center justify-center rounded-lg bg-white/5 text-white/60 transition-colors hover:bg-white/10 hover:text-white"
                >
                  <HiXMark className="h-4 w-4" strokeWidth={2} />
                </button>
              </div>
              {/* Chat only. The orb belongs to the launcher and the hero —
                  repeating it inside the panel just steals the space the
                  conversation needs. */}
              <div
                className={`ip-chat-dock h-full min-h-0 bg-[#0B1028] ${
                  fullscreen ? "ip-chat-dock--full" : ""
                }`}
              >
                <HaileyChat />
              </div>
            </motion.div>
          </>
        ) : null}
      </AnimatePresence>
    </>
  );
}
